/**
 * 授權申請「開窗起手值」的取值鏈（批114；工單 #5539／#5538）。
 *
 * ## 為什麼要抽成純函式
 *
 * `views/RequestsView.vue` 的 `openDetail` 原本只把 `r.plan` 設進套裝 chip，**其餘欄位
 * 沒有拿套裝模板當 fallback**（只有期限有）。於是「客戶第一次新發、草稿已選套裝」的情境
 * 下，這台主機沒有現行證 → 配額編輯器以 `absent_default`（多半是 0）起手並在掛載時回寫
 * 表單 → 一按儲存就把**全 0 的配額**存進草稿，核發時簽出一張 0 配額的證，還會被判成
 * `custom_content` 而升級成要審核。畫面上完全看不出哪裡不對（#5539）。
 *
 * 取值鏈與後端 `services/license_request.activate_request` 的 `_pick` **逐段對齊**：
 *
 * | 類型     | 順序                                   |
 * |----------|----------------------------------------|
 * | `new`    | 申請內容 → 套裝模板                     |
 * | `change` | 申請內容 → 套裝模板 → 現行證             |
 * | `renew`  | 申請內容 → 現行證（＝後端的 `_carry_over`）|
 *
 * ★ **`renew` 沒有套裝那一段**：續約走 `_activate_renew`，模組／配額／行為參數是由
 *   `_carry_over` 從被續約的那張證沿用，申請內容只是覆寫。這裡多補一段套裝，畫面顯示的
 *   就不是核發真的會簽出來的東西。
 *
 * ## 三個容易寫錯的判準
 *
 * 1. **模組的空 dict ＝沒指定**（後端 `empty_is_absent=True`）：portal 的變更申請一律送
 *    `modules: {}`（模組不由客戶改），當成「要簽一張沒有模組的證」會讓客戶功能全滅。
 * 2. **行為參數的空 dict ＝明確不帶**（工單 #5538）：`{}` 是「這些政策一個都不要」，
 *    只有 `null`／缺席才是「沒指定、往下找」。判準寫成 truthy 的下場就是 #5538——
 *    使用者把套裝帶進來的政策全部取消，畫面卻又把套裝預設值填回去。
 * 3. **套裝存在就停在套裝**（即使它的模組／政策是空的）：後端 `_pick` 只對**申請內容**
 *    套用 `empty_is_absent`，套裝那一段是 `plan_value is not None` 就採用。
 *
 * 配額另外多一件事：舊資料的一級數字欄要先用 `lib/seedQuotas.ts` 併進 `quotas`，否則
 * 批114 之前建的草稿（只有相容欄、沒有 `quotas`）一開窗數字就會消失。
 */
import type { PlanSpec, ProductSpec } from '@/api/ls'
import { LEGACY_COUNT_FIELDS, seedQuotas } from '@/lib/seedQuotas'

/**
 * 申請單投影上「相容數字欄」的欄名後綴。
 *
 * 同一個數字在三個地方有三種欄名：`request_json.requested` 裡是一級欄名、
 * `license_requests` 表上有的帶這個後綴（送件當下的快照欄）、有的同名。兩種都試，
 * 呼叫端就不必認得任何一個具體欄名（LS 前端的「零產品字面」規則）。
 */
const PROJECTION_SUFFIX = '_requested'

export interface RequestDraftSeed {
  /** 模組勾選（`ModulePicker` 的 v-model）；`{}`＝一個都不勾。 */
  modules: Record<string, string>
  /** 數量配額（`QuotaEditor` 的 v-model）；`null`＝不指定，交給後端從套裝展開。 */
  quotas: Record<string, number | null> | null
  /** 行為參數（`PolicyEditor` 的 v-model）；`{}`＝一個鍵都不帶。 */
  policy: Record<string, unknown>
}

export interface RequestDraftSeedInput {
  /** `new`｜`change`｜`renew`｜`addon`；缺席視同 `new`。 */
  kind?: string | null
  /** 該產品的憑證設計（`seedQuotas` 用它判斷哪些鍵是宣告過的）。 */
  spec?: ProductSpec | null
  /** 申請單的投影欄（直接丟整個 `LicenseReq`）。 */
  request?: unknown
  /**
   * `request_json.requested` 的原文。
   * ★ **呼叫端要先過讀端閘**：`policy`／`not_before`／`expires_at` 三鍵只有我方草稿
   *   （`origin='admin'`）讀得出來，與後端 `requested_content()` 對稱。
   */
  raw?: unknown
  /** 目前選到的套裝模板（`lib/products.ts::planByKey` 查好的）；null＝客製。 */
  plan?: PlanSpec | null
  /** 現行證摘要（`LicenseReq.current`）；null＝這台主機還沒有證。 */
  current?: unknown
}

function asObject(src: unknown): Record<string, unknown> {
  return src && typeof src === 'object' && !Array.isArray(src) ? (src as Record<string, unknown>) : {}
}

/** 純字串 map（模組是 `{key: 子項}`）；不是物件就回 null。 */
function asStringMap(v: unknown): Record<string, string> | null {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null
  return { ...(v as Record<string, string>) }
}

/** 「有內容的 map」——空 dict 視同沒給（模組的 `empty_is_absent`）。 */
function nonEmptyMap(v: unknown): Record<string, string> | null {
  const m = asStringMap(v)
  return m && Object.keys(m).length ? m : null
}

/** 「有給值的 map」——`{}` 是明確值（行為參數；工單 #5538）。 */
function givenMap(v: unknown): Record<string, unknown> | null {
  if (v == null) return null
  if (typeof v !== 'object' || Array.isArray(v)) return null
  return { ...(v as Record<string, unknown>) }
}

/** 配額包（`{鍵: 上限 | null}`）；不是物件就回 null＝這一段沒有配額包。 */
function asQuotaMap(v: unknown): Record<string, number | null> | null {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null
  return { ...(v as Record<string, number | null>) }
}

/**
 * 組出丟給 `seedQuotas` 的來源物件：`{quotas, <相容數字欄>…}`。
 *
 * 相容欄在每個來源上各試兩種欄名（一級欄名與帶後綴的投影欄名），取**第一個數字**。
 */
function quotaSource(quotas: unknown, sources: unknown[]): Record<string, unknown> {
  const out: Record<string, unknown> = { quotas }
  for (const field of Object.keys(LEGACY_COUNT_FIELDS)) {
    for (const src of sources) {
      const o = asObject(src)
      const v = o[field] ?? o[`${field}${PROJECTION_SUFFIX}`]
      if (typeof v === 'number') {
        out[field] = v
        break
      }
    }
  }
  return out
}

/**
 * 算出開窗時要帶進表單的模組／配額／行為參數。
 *
 * 純函式、不碰任何 store——測試直接餵資料（`lib/requestDraftSeed.spec.ts`）。
 */
export function seedRequestDraft(input: RequestDraftSeedInput): RequestDraftSeed {
  const kind = input.kind || 'new'
  const spec = input.spec ?? null
  const req = input.request
  const raw = input.raw
  const plan = input.plan ?? null
  const current = input.current ?? null
  // `renew` 不經套裝展開（後端走 `_carry_over`）；只有 `change`／`renew` 沿用現行證。
  const usePlan = kind !== 'renew'
  const useCurrent = kind === 'change' || kind === 'renew'

  const reqObj = asObject(req)
  const rawObj = asObject(raw)
  const curObj = asObject(current)

  // --- 模組：申請內容（非空）→ 套裝（有選就停）→ 現行證 → {} ------------------
  const modules =
    nonEmptyMap(reqObj.modules_requested) ??
    nonEmptyMap(rawObj.modules) ??
    (usePlan && plan ? (asStringMap(plan.modules) ?? {}) : null) ??
    (useCurrent ? asStringMap(curObj.modules) : null) ??
    {}

  // --- 配額：**兩條鏈**（與後端 `activate_request` 逐段對齊）------------------
  //
  // ① **配額包整包取代**：申請內容 → 套裝 → 現行證，第一個「真的有配額包」的段勝出。
  //    ★ 不是逐鍵合併：客戶把某個鍵改回「不限」時，合併會讓舊值把它蓋回去。
  // ② **相容數字欄各自一條鏈**：舊草稿／舊 portal 只送得出一級欄位，它要能蓋過 ①
  //    取到的套裝值——否則「加五席」的舊草稿會顯示套裝的席位數。
  //    每一段都先過 `seedQuotas`（把該段自己的一級欄併成同名配額鍵）再比對。
  const contentQuotas = reqObj.quotas_requested ?? rawObj.quotas
  const stages: unknown[] = [
    quotaSource(contentQuotas, [reqObj, rawObj]),
    usePlan ? plan : null,
    useCurrent ? current : null,
  ]
  const baseQuotas =
    asQuotaMap(contentQuotas) ??
    (usePlan && plan ? asQuotaMap(plan.quotas) : null) ??
    (useCurrent ? asQuotaMap(curObj.quotas) : null)
  const merged = stages.map((s) => (s ? seedQuotas(spec, s) : null))
  const declared = new Set((spec?.quotas ?? []).map((q) => q.key))
  let quotas: Record<string, number | null> | null = baseQuotas ? { ...baseQuotas } : null
  for (const key of Object.values(LEGACY_COUNT_FIELDS)) {
    if (!declared.has(key)) continue
    const hit = merged.find((m) => m !== null && key in m)
    if (!hit) continue
    quotas = { ...(quotas ?? {}), [key]: hit[key] }
  }

  // --- 行為參數：`{}` 是明確值，不再往下回退（#5538）------------------------
  const policy =
    givenMap(reqObj.policy) ??
    givenMap(rawObj.policy) ??
    (usePlan && plan ? { ...(plan.policies ?? {}) } : null) ??
    (useCurrent ? givenMap(curObj.policy) : null) ??
    {}

  return { modules, quotas, policy }
}
